import ModeButton from 'components/Buttons/ModeButton';
import { usePerms } from 'contexts/PermsContext';
import {
  selectSelectedDeviceId,
  selectSelectedDeviceIndex,
} from 'features/integrations/state/selectors/deviceSelectors';
import { DeviceIsDeviceType } from 'features/integrations/utils/deviceComparisionUtils';
import { useMemo, useState } from 'react';
import { Alert, Button, Card, Col, Container, Form, InputGroup, Row, Stack } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import {
  useChangeAwareDeviceSettingsMutation,
  useGetConnectedDevicesQuery,
} from 'services/devices/devices.api';
import DeviceSetting from './DeviceSetting';
import SettingButton from './SettingButton';

export default function AwareFloodDeviceSettings({ mobile = false }) {
  const perms = usePerms();
  const { data: connectedDevices = [] } = useGetConnectedDevicesQuery();
  const [changeAwareDeviceSettings] = useChangeAwareDeviceSettingsMutation();
  const selectedDeviceId = useSelector(selectSelectedDeviceId);

  const device = connectedDevices.find((device) => device.id === selectedDeviceId) || null;
  const [commandSent, setCommandSent] = useState(false);
  const [deviceSettings, setDeviceSettings] = useState({
    node_id: device?.node_id,
    uuid: device?.uuid,
    imei: device?.IMEI,
    depthDet1: device?.device_settings?.depthDet1,
    depthDet2: device?.device_settings?.depthDet2,
    depthDet3: device?.device_settings?.depthDet3,
    depthHoldHours: device?.device_settings?.depthHoldHours,
    ffTheshold: device?.device_settings?.ffTheshold || 0, // Use fallback value if null
    dropThresh: device?.device_settings?.dropThresh,
    mode: device?.device_settings?.mode,
    samp: device?.device_settings?.samp,
    dDetImgEnable: device?.device_settings?.dDetImgEnable,
    ffiImgEnable: device?.device_settings?.ffiImgEnable ?? 0, // Use fallback value if null
    dRateImgEnable: device?.device_settings?.dRateImgEnable,
    hResImgEnable: device?.device_settings?.hResImgEnable,
    water_level_adj: device?.device_settings?.water_level_adj,
    externalDevice: device?.device_settings?.externalDevice,
    volConstant: device?.device_settings?.volConstant,
    calibration: device?.device_settings?.calibration,
    resetCalibration: false,
    imgHoldEnable: device?.device_settings?.imgHoldEnable,
    imgHoldStart: device?.device_settings?.imgHoldStart,
    imgHoldEnd: device?.device_settings?.imgHoldEnd,
    imageRequest: false,
    gpsSync: false,
    ipAddress: device?.device_settings?.ipAddress,
    port: device?.device_settings?.port,
    reboot: false,
  });

  const [copyId, setCopyId] = useState(null);

  const [ipParts, setIpParts] = useState(
    deviceSettings?.ipAddress ? deviceSettings?.ipAddress.split('.') : [],
  );

  const handleIpPartChange = (partIndex, newPart) => {
    const newIpParts = [...ipParts];
    newIpParts[partIndex] = newPart;
    setIpParts(newIpParts);
    setDeviceSettings({
      ...deviceSettings,
      ipAddress: newIpParts.join('.'),
    });
  };

  const handleInputChange = (field, value) => {
    setDeviceSettings((prevSettings) => ({
      ...prevSettings,
      [field]: value,
    }));
  };

  const reportingModesOptions = {
    1: 'Auto',
    2: 'Fast',
    3: 'Slow',
  };
  const samplingModesOptions = { 0: '30 seconds', 1: '60 seconds' };

  const imagingModes = [
    {
      displayName: 'Depth Detection Imaging',
      settingName: 'dDetImgEnable',
    },
    { displayName: 'Rise Rate Imaging', settingName: 'ffiImgEnable' },
    { displayName: 'Drop Rate Imaging', settingName: 'dRateImgEnable' },
    {
      displayName: 'High Resolution Imaging',
      settingName: 'hResImgEnable',
    },
  ];

  const getChangedFields = (original = {}, updated = {}) => {
    let changes = {};

    for (let key in updated) {
      if (key === 'node_id' || key === 'imei' || key === 'uuid') {
        // Always include node_id, IMEI, and UUID
        changes[key] = updated[key];
        continue;
      }

      if (key === 'saltwater' || key === 'resetCalibration' || key === 'reboot') {
        // Always ignore
        continue;
      }

      if (original[key] === null && updated[key] === 0) {
        // Treat null in original and 0 in updated as unchanged for specific fields
        if (key === 'ffTheshold' || key === 'ffiImgEnable') {
          continue;
        }
      }

      if (updated[key] !== original[key]) {
        changes[key] = updated[key];
      }
    }

    return changes;
  };

  const sendCommand = async () => {
    try {
      const correctedSettings = {
        ...deviceSettings,
      };

      const changedFields = getChangedFields(device?.device_settings, correctedSettings);

      await changeAwareDeviceSettings(changedFields).unwrap();

      setCommandSent(true);

      setTimeout(() => {
        setCommandSent(false);
      }, 5000);
    } catch (error) {
      console.error(error.message);
    }
  };

  const canEditAware = perms.testPermission(
    [8, 9, 10, 11, 12, 13],
    ['Create-EditFloodStageConnections'],
  );
  const hasChanges = useMemo(() => {
    const changes = getChangedFields(device?.device_settings, deviceSettings);
    return Object.keys(changes).some((key) => !['node_id', 'imei', 'uuid'].includes(key));
  }, [device?.device_settings, deviceSettings]);

  const cardClassName = 'bg-primary-highlight text-white border-1';
  const cardHeaderClassName = 'bg-primary text-white';
  const cardBodyClassName = 'bg-base rounded-2';
  const fieldClassName = 'bg-primary text-white border-0';

  const formatHour = (value) => `${String(value ?? 0).padStart(2, '0')}:00`;

  if (mobile) {
    return (
      <Alert variant="warning" className="m-3">
        This feature is currently not implemented. Please manage Flood Stage devices from the
        desktop website.
      </Alert>
    );
  }

  return (
    <Container fluid className="py-3 text-white">
      <div className="d-flex flex-column gap-3 min-h-75vh">
        {canEditAware && (
          <div className="d-flex flex-column flex-lg-row align-items-lg-center justify-content-between gap-2 bg-primary-shadow border border-1 border-black rounded-3 p-3">
            <div className="fw-semibold">Copy settings from</div>
            <Form.Select
              className={fieldClassName}
              value={copyId ?? ''}
              onChange={(e) => {
                const value = e.target.value;
                if (!value) {
                  setCopyId(null);
                  return;
                }

                const selectedOption = connectedDevices.find(
                  (node) => node.id === Number(value),
                )?.device_settings;

                setCopyId(value);

                if (!selectedOption) return;

                setDeviceSettings((prevSettings) => ({
                  ...prevSettings,
                  depthDet1: selectedOption?.depthDet1,
                  depthDet2: selectedOption?.depthDet2,
                  depthDet3: selectedOption?.depthDet3,
                  depthHoldHours: selectedOption?.depthHoldHours,
                  ffTheshold: selectedOption?.ffTheshold,
                  dropThresh: selectedOption?.dropThresh,
                  mode: selectedOption?.mode,
                  samp: selectedOption?.samp,
                  dDetImgEnable: selectedOption?.dDetImgEnable,
                  ffiImgEnable: selectedOption?.ffiImgEnable ?? 0,
                  dRateImgEnable: selectedOption?.dRateImgEnable,
                  hResImgEnable: selectedOption?.hResImgEnable,
                }));
              }}
            >
              <option value="">Select device...</option>
              {connectedDevices
                .filter((node) => DeviceIsDeviceType(node, 'Stage'))
                .map((node) => (
                  <option key={node.id} value={node.id}>
                    {(node.device_type !== 'Manual Road Closure' ? node.static_id + ' ' : '') +
                      node.alias}
                  </option>
                ))}
            </Form.Select>
          </div>
        )}
        {device && (
          <Row className="gy-4 mt-1">
            <Col lg={6} className="d-flex flex-column gap-4">
              <Card className={`shadow-sm ${cardClassName}`}>
                <Card.Header className={cardHeaderClassName}>Threshold Settings</Card.Header>
                <Card.Body className={`d-flex flex-column gap-2 ${cardBodyClassName}`}>
                  <DeviceSetting
                    settingName="Depth Detection Threshold 1"
                    settingDataName="depthDet1"
                    setting={deviceSettings?.depthDet1}
                    metric={'inches'}
                    min={1}
                    max={254}
                    turnOffAt={255}
                    deviceSettings={deviceSettings}
                    handleInputChange={handleInputChange}
                    device={device}
                    type={'int'}
                    enabled={canEditAware}
                    restoreValue={device.device_settings?.depthDet1}
                  />
                  <DeviceSetting
                    settingName="Depth Detection Threshold 2"
                    settingDataName="depthDet2"
                    setting={deviceSettings?.depthDet2}
                    metric={'inches'}
                    min={1}
                    max={254}
                    turnOffAt={255}
                    deviceSettings={deviceSettings}
                    handleInputChange={handleInputChange}
                    device={device}
                    type={'int'}
                    enabled={canEditAware}
                    restoreValue={device.device_settings?.depthDet2}
                  />
                  <DeviceSetting
                    settingName="Depth Detection Threshold 3"
                    settingDataName="depthDet3"
                    setting={deviceSettings?.depthDet3}
                    metric={'inches'}
                    min={1}
                    max={254}
                    turnOffAt={255}
                    deviceSettings={deviceSettings}
                    handleInputChange={handleInputChange}
                    device={device}
                    type={'int'}
                    enabled={canEditAware}
                    restoreValue={device.device_settings?.depthDet3}
                  />
                  <DeviceSetting
                    settingName="Depth Detection Holdoff"
                    settingDataName="depthHoldHours"
                    setting={deviceSettings?.depthHoldHours}
                    metric={'hours'}
                    min={0}
                    max={24}
                    turnOffAt={0}
                    deviceSettings={deviceSettings}
                    handleInputChange={handleInputChange}
                    device={device}
                    enabled={canEditAware}
                    restoreValue={device.device_settings?.depthHoldHours}
                  />
                  <DeviceSetting
                    settingName="Rise Rate Threshold"
                    settingDataName="ffTheshold"
                    setting={(deviceSettings?.ffTheshold * 60).toFixed(1)}
                    metric={'in/min'}
                    min={0}
                    max={(2.54 * 60).toFixed(1)}
                    turnOffAt={(2.55 * 60).toFixed(1)}
                    step={0.6}
                    deviceSettings={deviceSettings}
                    handleInputChange={(field, value) =>
                      handleInputChange(field, parseFloat((value / 60).toFixed(2)))
                    }
                    device={device}
                    type={'float'}
                    enabled={canEditAware}
                    restoreValue={Number(
                      ((device?.device_settings?.ffTheshold ?? 0) * 60).toFixed(1),
                    )}
                  />
                  <DeviceSetting
                    settingName="Drop Rate Threshold"
                    settingDataName="dropThresh"
                    setting={(deviceSettings?.dropThresh * 60).toFixed(1)}
                    metric={'in/min'}
                    min={0}
                    max={(2.54 * 60).toFixed(1)}
                    turnOffAt={(2.55 * 60).toFixed(1)}
                    step={0.6}
                    deviceSettings={deviceSettings}
                    handleInputChange={(field, value) =>
                      handleInputChange(field, parseFloat((value / 60).toFixed(2)))
                    }
                    device={device}
                    type={'float'}
                    enabled={canEditAware}
                    restoreValue={Number(
                      ((device?.device_settings?.dropThresh ?? 0) * 60).toFixed(1),
                    )}
                  />
                </Card.Body>
              </Card>
              <Card className={`shadow-sm ${cardClassName}`}>
                <Card.Header className={cardHeaderClassName}>Modes</Card.Header>
                <Card.Body className={`d-flex flex-column gap-3 ${cardBodyClassName}`}>
                  <Row className="align-items-center g-3">
                    <Col lg={5} className="d-flex align-items-center gap-2">
                      <Form.Label className="fw-semibold mb-0">Reporting Modes</Form.Label>
                    </Col>
                    <Col lg={7}>
                      <ModeButton
                        options={Object.values(reportingModesOptions)}
                        onChange={(value) => {
                          setDeviceSettings({
                            ...deviceSettings,
                            mode: parseInt(
                              Object.keys(reportingModesOptions).find(
                                (key) => reportingModesOptions[key] === value,
                              ),
                            ),
                          });
                        }}
                        selectedOption={reportingModesOptions[deviceSettings?.mode]}
                        enabled={canEditAware}
                      />
                    </Col>
                  </Row>
                  <Row className="align-items-center g-3">
                    <Col lg={5} className="d-flex align-items-center gap-2">
                      <Form.Label className="fw-semibold mb-0">Sampling Modes</Form.Label>
                    </Col>
                    <Col lg={7}>
                      <ModeButton
                        options={Object.values(samplingModesOptions)}
                        onChange={(value) => {
                          setDeviceSettings({
                            ...deviceSettings,
                            samp: parseInt(
                              Object.keys(samplingModesOptions).find(
                                (key) => samplingModesOptions[key] === value,
                              ),
                            ),
                          });
                        }}
                        selectedOption={samplingModesOptions[deviceSettings?.samp]}
                        enabled={canEditAware}
                      />
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
              <Card className={`shadow-sm ${cardClassName}`}>
                <Card.Header className={cardHeaderClassName}>Imaging Modes</Card.Header>
                <Card.Body className={`d-flex flex-column gap-2 ${cardBodyClassName}`}>
                  {imagingModes.map(({ displayName, settingName }) => (
                    <SettingButton
                      key={settingName}
                      displayName={displayName}
                      settingName={settingName}
                      deviceSettings={deviceSettings}
                      setDeviceSettings={setDeviceSettings}
                      device={device}
                      enabled={canEditAware}
                    />
                  ))}
                </Card.Body>
              </Card>
            </Col>
            <Col lg={6} className="d-flex flex-column gap-4">
              <Card className={`shadow-sm ${cardClassName}`}>
                <Card.Header className={cardHeaderClassName}>Additional Settings</Card.Header>
                <Card.Body className={`d-flex flex-column gap-3 ${cardBodyClassName}`}>
                  <SettingButton
                    displayName={'External Device'}
                    settingName={'externalDevice'}
                    deviceSettings={deviceSettings}
                    setDeviceSettings={setDeviceSettings}
                    device={device}
                    enabled={canEditAware}
                  />

                  <SettingButton
                    displayName={'Triggered Imaging Holdoff'}
                    settingName={'imgHoldEnable'}
                    deviceSettings={deviceSettings}
                    setDeviceSettings={setDeviceSettings}
                    device={device}
                    enabled={canEditAware}
                  />

                  <Form.Group as={Row} className="align-items-center g-2">
                    <Form.Label column lg={5} className="mb-0">
                      Hour Start
                    </Form.Label>
                    <Col lg={7}>
                      <Form.Control
                        className={fieldClassName}
                        type="time"
                        value={formatHour(deviceSettings?.imgHoldStart)}
                        disabled={!deviceSettings?.imgHoldEnable || !canEditAware}
                        onChange={(e) =>
                          setDeviceSettings({
                            ...deviceSettings,
                            imgHoldStart: parseInt(e.target.value.split(':')[0]),
                          })
                        }
                      />
                    </Col>
                  </Form.Group>

                  <Form.Group as={Row} className="align-items-center g-2">
                    <Form.Label column lg={5} className="mb-0">
                      Hour End
                    </Form.Label>
                    <Col lg={7}>
                      <Form.Control
                        className={fieldClassName}
                        type="time"
                        value={formatHour(deviceSettings?.imgHoldEnd)}
                        disabled={!deviceSettings?.imgHoldEnable || !canEditAware}
                        onChange={(e) =>
                          setDeviceSettings({
                            ...deviceSettings,
                            imgHoldEnd: parseInt(e.target.value.split(':')[0]),
                          })
                        }
                      />
                    </Col>
                  </Form.Group>

                  <Form.Group as={Row} className="align-items-center g-2">
                    <Form.Label column lg={5} className="mb-0">
                      Water Level Adj.
                    </Form.Label>
                    <Col lg={7} className="d-flex justify-content-lg-end">
                      <InputGroup style={{ width: 140 }}>
                        <Form.Control
                          className="bg-primary border-0"
                          type="number"
                          value={deviceSettings?.water_level_adj ?? ''}
                          disabled={!canEditAware}
                          onChange={(e) =>
                            handleInputChange(
                              'water_level_adj',
                              e.target.value === '' ? '' : parseFloat(e.target.value),
                            )
                          }
                          style={{ textAlign: 'left' }}
                        />
                        <InputGroup.Text className="text-dark bg-white border-0 px-1">
                          inches
                        </InputGroup.Text>
                      </InputGroup>
                    </Col>
                  </Form.Group>
                </Card.Body>
              </Card>
              <Card className={`shadow-sm ${cardClassName}`}>
                <Card.Header className={cardHeaderClassName}>Action Requests</Card.Header>
                <Card.Body className={`d-flex flex-column gap-3 ${cardBodyClassName}`}>
                  <SettingButton
                    displayName={'Image Request'}
                    settingName={'imageRequest'}
                    deviceSettings={deviceSettings}
                    setDeviceSettings={setDeviceSettings}
                    device={device}
                    enabled={canEditAware}
                  />
                  <SettingButton
                    displayName={'GPS Sync'}
                    settingName={'gpsSync'}
                    deviceSettings={deviceSettings}
                    setDeviceSettings={setDeviceSettings}
                    device={device}
                    enabled={canEditAware}
                  />
                </Card.Body>
              </Card>
              <Card className="shadow-sm bg-primary-shadow text-white border border-danger">
                <Card.Header className="bg-danger text-white">Advanced Settings</Card.Header>
                <Card.Body className={`d-flex flex-column gap-3 ${cardBodyClassName}`}>
                  <p className="text-warning fw-semibold mb-0">
                    Do not modify unless directed by RescAlert support.
                  </p>

                  <Form.Group as={Row} className="align-items-center g-2">
                    <Form.Label column lg={5} className="mb-0">
                      Destination IP Address
                    </Form.Label>
                    <Col lg={7}>
                      <Stack direction="horizontal" gap={2}>
                        {Array.from({ length: 4 }).map((_, index) => (
                          <Form.Control
                            className={`${fieldClassName} text-center`}
                            key={index}
                            type="number"
                            min={0}
                            max={255}
                            step={1}
                            disabled={!canEditAware}
                            value={ipParts[index] ?? ''}
                            onChange={(e) => {
                              const numericValue = Math.min(
                                Math.max(Number(e.target.value) || 0, 0),
                                255,
                              );
                              handleIpPartChange(index, numericValue);
                            }}
                          />
                        ))}
                      </Stack>
                    </Col>
                  </Form.Group>

                  <Form.Group as={Row} className="align-items-center g-2">
                    <Form.Label column lg={5} className="mb-0">
                      Destination Port
                    </Form.Label>
                    <Col lg={7}>
                      <Form.Control
                        className={fieldClassName}
                        type="number"
                        step={1}
                        disabled={!canEditAware}
                        value={deviceSettings?.port}
                        onChange={(e) => {
                          setDeviceSettings({
                            ...deviceSettings,
                            port: e.target.value,
                          });
                        }}
                      />
                    </Col>
                  </Form.Group>

                  <Form.Group as={Row} className="align-items-center g-2">
                    <Form.Label column lg={5} className="mb-0 text-danger fw-semibold">
                      Reboot
                    </Form.Label>
                    <Col lg={7} className="d-flex justify-content-lg-end">
                      <Form.Check
                        type="switch"
                        id="device-reboot-toggle"
                        disabled={!canEditAware}
                        checked={deviceSettings?.reboot}
                        onChange={(e) => {
                          const value = e.target.checked;
                          setDeviceSettings((prevSettings) => ({
                            ...prevSettings,
                            reboot: value,
                          }));
                        }}
                      />
                    </Col>
                  </Form.Group>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}
        {canEditAware && (
          <div className="d-flex justify-content-end align-items-center gap-3">
            {commandSent && (
              <span className="text-success fw-semibold">Command sent to server!</span>
            )}
            <Button
              size="lg"
              onClick={sendCommand}
              disabled={!hasChanges}
              variant={hasChanges ? 'primary' : 'secondary'}
              className="aware-save-button"
            >
              Save
            </Button>
          </div>
        )}
      </div>
    </Container>
  );
}
